import { combinedUMUIM, offerExcessUM, noUmbrellaStates } from '@ourbranch/lookups';

type Overrides = { allowUmbrella?: boolean | null; allow300KCSL?: boolean | null } | null | undefined;
type Driver = {
  excludeDriver?: boolean | null;
  hasViolationsBlockingUmbrella?: boolean | null;
  hasMajorViolationBlockingUmbrella?: boolean | null;
  hasAtFaultAccidentBlockingUmbrella?: boolean | null;
};
type UmbrellaInputs = {
  policyLimitBIPD?: string | null;
  coverageX?: number | null;
  drivers?: Driver[] | null;
  renewalVariables?: Overrides;
};
type UMInputs = { policyLimitUMBI?: string | null; policyLimitUIMBI?: string | null; state: string };

export const hasCoverageEligible = (coverage: string[]) => {
  if (coverage.length > 1) {
    return Number(coverage[0]) >= 250 && Number(coverage[1]) >= 500;
  }
  // CSL
  return Number(coverage[0]) >= 500;
};

export const canHaveExcessUM = ({ policyLimitUMBI, policyLimitUIMBI, state }: UMInputs) => {
  if (!policyLimitUMBI || !policyLimitUIMBI) {
    return false;
  }
  // must be 250/500 or 500CSL & in bix state

  const limitUMBI = policyLimitUMBI.includes('/') ? policyLimitUMBI.split('/') : policyLimitUMBI.split(' ').slice(0, 1);
  const limitUIMBI = policyLimitUIMBI.includes('/')
    ? policyLimitUIMBI.split('/')
    : policyLimitUIMBI.split(' ').slice(0, 1);
  return combinedUMUIM[state as keyof typeof combinedUMUIM]
    ? hasCoverageEligible(limitUMBI)
    : hasCoverageEligible(limitUMBI) && hasCoverageEligible(limitUIMBI);
};

export const canAddUmbrellaUMUIM = ({ policyLimitUMBI, policyLimitUIMBI, state }: UMInputs) => {
  if (state === 'NH') {
    // Everything all the time in New Hampshire!
    return true;
  }
  return canHaveExcessUM({ policyLimitUMBI, policyLimitUIMBI, state });
};

export const getUmbrellaBIMinimum = (renewalVariables?: Overrides) => (renewalVariables?.allow300KCSL ? 300 : 500);

// Per-requirement breakdown behind canAddUmbrella's fixable gates, for the umbrella
// section's disabled-state checklist. Deliberately excludes the prior-carrier gate
// (see isUmbrellaBlockedByPriorCarrier) and the allowUmbrella override.
// policyLimitBIPD ids are 'BIperson/BIoccurrence/PD' in thousands (e.g. '250/500/100'),
// or 'NNN CSL' for combined single limits (no separate PD portion)
export const parseBIPDLimits = (policyLimitBIPD?: string | null) => {
  if (!policyLimitBIPD || !/^(?:\d+\/\d+(?:\/\d+)?|\d+ CSL)$/.test(policyLimitBIPD)) {
    return null;
  }
  const raw = String(policyLimitBIPD);
  const isCSL = raw.includes('CSL');
  const parts = raw.split('/');
  const limitBI = Number.parseInt(isCSL ? raw.split(' ')[0] : parts[1], 10);
  const limitPD = isCSL || parts.length < 3 ? null : Number.parseInt(parts[2], 10);
  return Number.isNaN(limitBI) ? null : { isCSL, limitBI, limitPD };
};

export const getUmbrellaLimitBIOccurrence = (policyLimitBIPD?: string | null) => {
  if (!policyLimitBIPD) {
    return undefined;
  }
  if (policyLimitBIPD.includes('CSL')) {
    return policyLimitBIPD.replace(' CSL', '');
  }
  return Number.parseInt(policyLimitBIPD.split('/')[0], 10) < 500 ? '300' : '500';
};

export const getUmbrellaRequirements = ({ policyLimitBIPD, coverageX, drivers, renewalVariables }: UmbrellaInputs) => {
  const autoLimits = parseBIPDLimits(policyLimitBIPD);
  // keep in sync with canAddUmbrella's allow300KCSL override
  const autoBILimit = !!autoLimits && autoLimits.limitBI >= getUmbrellaBIMinimum(renewalVariables);
  const allDrivers = drivers || [];
  return {
    activeAutoPolicy: !!autoLimits,
    autoBILimit,
    homeownersLiability: (coverageX || 0) >= 300000,
    noExcludedDrivers: allDrivers.every((d) => !d.excludeDriver),
    // Records rated before #35652 only have the combined flag.
    noMajorViolationOrUDR: allDrivers.every(
      (d) => !(d.hasMajorViolationBlockingUmbrella ?? d.hasViolationsBlockingUmbrella)
    ),
    noAtFaultAccidentBlockingUmbrella: allDrivers.every((d) => !d.hasAtFaultAccidentBlockingUmbrella)
  };
};

export const canAddUmbrella = (inputs: UmbrellaInputs) =>
  !!inputs.renewalVariables?.allowUmbrella || Object.values(getUmbrellaRequirements(inputs)).every(Boolean);

// General overrides never bypass excess UM/UIM or state availability.
export const getUmbrellaEligibility = (
  inputs: UmbrellaInputs & UMInputs & { uninsuredMotoristLimit?: string | null }
) => {
  const general = getUmbrellaRequirements(inputs);
  const override = !!inputs.renewalVariables?.allowUmbrella;
  const excessUM = !!inputs.uninsuredMotoristLimit && inputs.uninsuredMotoristLimit !== '0';
  const requirements = {
    activeAutoPolicy: override || general.activeAutoPolicy,
    autoBILimit: override || general.autoBILimit,
    homeownersLiability: override || general.homeownersLiability,
    noExcludedDrivers: override || general.noExcludedDrivers,
    noMajorViolationOrUDR: override || general.noMajorViolationOrUDR,
    noAtFaultAccidentBlockingUmbrella: override || general.noAtFaultAccidentBlockingUmbrella,
    stateAllowsUmbrella: !noUmbrellaStates[inputs.state as keyof typeof noUmbrellaStates],
    autoUMUIM: !excessUM || canAddUmbrellaUMUIM(inputs),
    stateAllowsExcessUM: !excessUM || !!offerExcessUM[inputs.state as keyof typeof offerExcessUM]
  };
  return {
    requirements,
    eligible: Object.values(requirements).every(Boolean),
    canEstimate:
      requirements.homeownersLiability && requirements.stateAllowsUmbrella && requirements.stateAllowsExcessUM
  };
};
